import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Spinner } from "../components/common";

const EMPTY = { line1: "", line2: "", city: "", state: "", zip: "", country: "United States" };

export default function AddressPage() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState(user.address || EMPTY);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const inp = "w-full h-12 px-4 rounded-xl bg-secondary border border-transparent focus:border-primary outline-none text-sm";

  const save = async () => {
    if (!f.line1 || !f.city || !f.state || !f.zip || !f.country) { toast.error("Fill in the required fields"); return; }
    setBusy(true);
    try { const { data } = await api.put("/auth/address", f); setUser(data); toast.success("Address saved"); nav(-1); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try { const { data } = await api.delete("/auth/address"); setUser(data); toast.success("Address removed"); setF(EMPTY); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="animate-fade-up" data-testid="address-page">
      <PageHeader title="Home Address" subtitle="Used for statements and to ship your physical card" />

      <div className="rounded-3xl bg-card border border-border p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="grid place-items-center w-10 h-10 rounded-full bg-primary/10 text-primary shrink-0"><MapPin size={18} /></div>
          <div>
            <h3 className="font-display text-base font-semibold leading-tight">Where do you live?</h3>
            <p className="text-[12px] text-muted-foreground leading-snug mt-0.5">Keep it accurate so your statements and card arrive correctly.</p>
          </div>
        </div>
        <div className="space-y-3">
          <input data-testid="addr-line1" placeholder="Street address" value={f.line1} onChange={set("line1")} className={inp} />
          <input data-testid="addr-line2" placeholder="Apt, suite (optional)" value={f.line2} onChange={set("line2")} className={inp} />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="addr-city" placeholder="City" value={f.city} onChange={set("city")} className={inp} />
            <input data-testid="addr-state" placeholder="State" value={f.state} onChange={set("state")} className={inp} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="addr-zip" placeholder="ZIP code" value={f.zip} onChange={set("zip")} className={inp} />
            <input data-testid="addr-country" placeholder="Country" value={f.country} onChange={set("country")} className={inp} />
          </div>
        </div>
        <button data-testid="addr-save" onClick={save} disabled={busy}
          className="w-full h-12 mt-5 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Spinner className="w-5 h-5" /> : "Save Address"}
        </button>
        {user.address && (
          <button data-testid="addr-remove" onClick={remove} disabled={busy} className="w-full h-11 mt-2 rounded-xl text-destructive font-semibold text-sm">Remove address</button>
        )}
      </div>
    </div>
  );
}
